import type { GridPoint } from '../core/types';
import type { WorldMap } from '../world/WorldMap';

export interface SelectionState {
  /** Selected tile (Phase 1 behaviour, unchanged). */
  readonly selected: GridPoint | null;
  readonly hovered: GridPoint | null;
  /** Building occupying the selected tile, if any (Phase 2). */
  readonly building: number | null;
  /** Building occupying the hovered tile, if any. */
  readonly hoveredBuilding: number | null;
}

export type SelectionListener = (state: SelectionState) => void;

/** Resolves which building (id) occupies a tile; null when free. */
export type BuildingLookup = (x: number, y: number) => number | null;

/**
 * Engine-independent selection / hover state. Clicking a tile always selects
 * that tile; if a building stands on it, that building becomes the selected
 * building too. Later phases extend this to units.
 */
export class SelectionSystem {
  private selected: GridPoint | null = null;
  private hovered: GridPoint | null = null;
  private building: number | null = null;
  private hoveredBuilding: number | null = null;
  private readonly listeners = new Set<SelectionListener>();

  constructor(
    private readonly map: WorldMap,
    private readonly buildingAt: BuildingLookup = () => null,
  ) {}

  get state(): SelectionState {
    return { selected: this.selected, hovered: this.hovered, building: this.building, hoveredBuilding: this.hoveredBuilding };
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  subscribe(l: SelectionListener): () => void {
    this.listeners.add(l);
    l(this.state);
    return () => this.listeners.delete(l);
  }

  /** Select a tile. Out-of-map clicks clear the selection. Returns the new selected tile. */
  select(tile: GridPoint | null): GridPoint | null {
    const next = tile && this.map.inBounds(tile.x, tile.y) ? { x: tile.x, y: tile.y } : null;
    const nextBuilding = next ? this.buildingAt(next.x, next.y) : null;
    if (!samePoint(next, this.selected) || nextBuilding !== this.building) {
      this.selected = next;
      this.building = nextBuilding;
      this.notify();
    }
    return this.selected;
  }

  hover(tile: GridPoint | null): void {
    const next = tile && this.map.inBounds(tile.x, tile.y) ? { x: tile.x, y: tile.y } : null;
    const nextBuilding = next ? this.buildingAt(next.x, next.y) : null;
    if (!samePoint(next, this.hovered) || nextBuilding !== this.hoveredBuilding) {
      this.hovered = next;
      this.hoveredBuilding = nextBuilding;
      this.notify();
    }
  }

  /** Re-resolve buildings under the selected / hovered tiles after buildings changed. */
  refresh(): void {
    const b = this.selected ? this.buildingAt(this.selected.x, this.selected.y) : null;
    const hb = this.hovered ? this.buildingAt(this.hovered.x, this.hovered.y) : null;
    if (b !== this.building || hb !== this.hoveredBuilding) {
      this.building = b;
      this.hoveredBuilding = hb;
      this.notify();
    }
  }

  clear(): void {
    this.select(null);
  }

  private notify(): void {
    const s = this.state;
    for (const l of this.listeners) l(s);
  }
}

function samePoint(a: GridPoint | null, b: GridPoint | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.x === b.x && a.y === b.y;
}
